import { ReactNode, useMemo, useState } from "react";
import { alpha, Box, Button, CircularProgress, Grid, Tooltip, Typography } from "@mui/material";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { executeRetryTaskRequest } from "api/common/tasks.api";
import IotVisionChip from "components/common/IotVisionChip";
import IotVisionTable from "components/common/IotVisionTable";
import IotVisionDialog from "components/dialogs/IotVisionDialog";
import { getScheduleTypeDisplay } from "configs/schedules";
import { getTaskStatusDisplay } from "configs/tasks";
import { setNotifier } from "context/notification";
import {
    EM_DASH,
    NO_END_LABEL,
    NO_TASKS_MESSAGE,
    REQUEUE_BUTTON_COLOR,
    SCHEDULE_ID_COPIED,
    TASK_QUEUED_FOR_RETRY,
} from "constants/schedule-monitor.constants";
import { ScheduleStatus } from "enums/schedule.enum";
import { TaskStatus } from "enums/task.enum";
import { IScheduleRowViewModel } from "types/schedule";
import { ITaskRowViewModel } from "types/task";
import deriveScheduleStatus, { findDueTask } from "utils/derive-schedule-status";
import { DataViewDialog, DateTimeCell, ScheduleStatusChip } from "./schedule-table";
import useScheduleTasks from "./use-schedule-tasks";

const displayOrDash = (value: string | null | undefined): string => value || EM_DASH;

const TaskStatusChip = ({ status, label }: { status: string; label: string }): JSX.Element => {
    const display = getTaskStatusDisplay(status);
    return (
        <IotVisionChip
            label={label.toUpperCase()}
            size="small"
            sx={{
                minWidth: "auto",
                backgroundColor: display.backgroundColor,
                color: display.color,
                border: `1px solid ${display.color}`,
                height: "24px",
                letterSpacing: "0.5px",
                fontWeight: 600,
                "& .MuiChip-label": {
                    padding: "0 10px",
                    display: "flex",
                    justifyContent: "center",
                },
            }}
        />
    );
};

const RetriedTaskNotice = ({
    retryScheduleId,
    onCopyScheduleId,
}: {
    retryScheduleId: string;
    onCopyScheduleId: (scheduleId: string) => void;
}): JSX.Element => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <IotVisionChip
            label="RETRIED"
            size="small"
            sx={{ minWidth: "auto", height: "24px", fontWeight: 600, letterSpacing: "0.5px" }}
        />
        <Typography
            variant="caption"
            fontFamily="monospace"
            color="text.secondary"
            noWrap
            sx={{ maxWidth: 110 }}
            title={retryScheduleId}
        >
            {retryScheduleId}
        </Typography>
        <Tooltip title="Copy new schedule ID">
            <Button
                size="small"
                onClick={() => onCopyScheduleId(retryScheduleId)}
                sx={{ minWidth: "auto", height: "24px", padding: "0 4px" }}
            >
                <ContentCopyOutlinedIcon sx={{ fontSize: "14px" }} />
            </Button>
        </Tooltip>
    </Box>
);

const RetryButton = ({
    taskId,
    retrying,
    onRetry,
}: {
    taskId: string;
    retrying: boolean;
    onRetry: (taskId: string) => void;
}): JSX.Element => (
    <Button
        size="small"
        variant="contained"
        disabled={retrying}
        startIcon={
            retrying ? (
                <CircularProgress size={12} sx={{ color: "#FFC94D" }} />
            ) : (
                <BoltOutlinedIcon sx={{ color: "#FFC94D !important", fontSize: "14px !important" }} />
            )
        }
        onClick={() => onRetry(taskId)}
        sx={{
            minWidth: "auto",
            height: "24px",
            fontSize: "11px",
            padding: "0 8px",
            whiteSpace: "nowrap",
            backgroundColor: REQUEUE_BUTTON_COLOR,
            boxShadow: `0 0 10px 1px ${alpha(REQUEUE_BUTTON_COLOR, 0.55)}`,
            "&:hover": {
                backgroundColor: REQUEUE_BUTTON_COLOR,
                boxShadow: `0 0 14px 2px ${alpha(REQUEUE_BUTTON_COLOR, 0.7)}`,
            },
            "&.Mui-disabled": {
                backgroundColor: REQUEUE_BUTTON_COLOR,
                opacity: 0.6,
            },
        }}
    >
        Retry
    </Button>
);

const TaskHistoryPlaceholder = ({ message }: { message: string }): JSX.Element => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 2 }}>
        <Typography variant="body2" color="text.secondary">
            {message}
        </Typography>
    </Box>
);

const MetaField = ({ label, children }: { label: string; children: ReactNode }): JSX.Element => (
    <Grid item xs={6} sm={3}>
        <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ textTransform: "uppercase", letterSpacing: "0.4px", fontSize: "12px" }}
        >
            {label}
        </Typography>
        <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Grid>
);

interface IScheduleDetailModalProps {
    row: IScheduleRowViewModel | null;
    open: boolean;
    onClose: () => void;
}

const ScheduleDetailModal = ({ row, open, onClose }: IScheduleDetailModalProps): JSX.Element | null => {
    const {
        rows: taskRows,
        loading: tasksLoading,
        error: tasksError,
        refetch: refetchTasks,
    } = useScheduleTasks(row?.id ?? null);
    const [selectedTaskData, setSelectedTaskData] = useState<Record<string, unknown> | null>(null);
    const [taskDataDialogOpen, setTaskDataDialogOpen] = useState(false);
    const [retryingTaskId, setRetryingTaskId] = useState<string | null>(null);

    const handleRetry = async (taskId: string): Promise<void> => {
        setRetryingTaskId(taskId);
        try {
            await executeRetryTaskRequest(taskId);
            setNotifier()({ type: "success", message: TASK_QUEUED_FOR_RETRY, isAutoClose: true });
            refetchTasks();
        } catch {
            // Server error message is already surfaced via the api client's notification bar.
        } finally {
            setRetryingTaskId(null);
        }
    };

    const handleCopyScheduleId = async (scheduleId: string): Promise<void> => {
        await navigator.clipboard.writeText(scheduleId);
        setNotifier()({ type: "success", message: SCHEDULE_ID_COPIED, isAutoClose: true });
    };

    const taskHistoryColumns: GridColDef<ITaskRowViewModel>[] = useMemo(
        () => [
            {
                field: "scheduledDateTime",
                headerName: "Scheduled",
                flex: 1,
                minWidth: 140,
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<ITaskRowViewModel>) => (
                    <DateTimeCell date={params.row.displayScheduledDateTime} />
                ),
            },
            {
                field: "typeLabel",
                headerName: "Type",
                flex: 1,
                minWidth: 140,
                sortable: false,
                disableColumnMenu: true,
            },
            {
                field: "statusLabel",
                headerName: "Status",
                flex: 1,
                minWidth: 120,
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<ITaskRowViewModel>) => {
                    const isFailed = params.row.statusCode === TaskStatus.FAILED;
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <TaskStatusChip status={params.row.statusCode} label={params.row.statusLabel} />
                            {isFailed && params.row.retryScheduleId ? (
                                <RetriedTaskNotice
                                    retryScheduleId={params.row.retryScheduleId}
                                    onCopyScheduleId={handleCopyScheduleId}
                                />
                            ) : null}
                            {isFailed && !params.row.retryScheduleId ? (
                                <RetryButton
                                    taskId={params.row.id}
                                    retrying={retryingTaskId === params.row.id}
                                    onRetry={handleRetry}
                                />
                            ) : null}
                        </Box>
                    );
                },
            },
            {
                field: "createdAt",
                headerName: "Created",
                flex: 1,
                minWidth: 140,
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<ITaskRowViewModel>) => (
                    <DateTimeCell date={params.row.createdAt} />
                ),
            },
            {
                field: "data",
                headerName: "Data",
                flex: 0.8,
                minWidth: 110,
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<ITaskRowViewModel>) =>
                    params.row.data ? (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                setSelectedTaskData(params.row.data);
                                setTaskDataDialogOpen(true);
                            }}
                        >
                            {"{} view"}
                        </Button>
                    ) : (
                        EM_DASH
                    ),
            },
        ],
        [retryingTaskId],
    );

    if (!row) return null;

    const { icon: TypeIcon, iconColor } = getScheduleTypeDisplay(row.typeCode);
    // Task history is fetched alongside this row's own state, so the derived status is only
    // meaningful once it has loaded - showing it mid-fetch would read "Unknown" for a schedule
    // that turns out to have plenty of task history.
    // taskRows is sorted newest-first by scheduledDateTime, which can put a pre-queued task for a
    // future run ahead of the last one actually attempted - only a task whose run is already due
    // can stand in for "the most recent task" (mirrors use-latest-task-by-schedule-id.ts).
    const now = new Date();
    const dueTask = findDueTask(taskRows, now);
    const derivedStatus = tasksLoading ? null : deriveScheduleStatus(row, dueTask, now);
    // Mirrors schedule-monitor-page.tsx's hasNextTaskById: only meaningful for a schedule the API
    // still reports as active, and only once task data has loaded.
    const hasNextTask =
        row.status === ScheduleStatus.ACTIVE && !tasksLoading
            ? taskRows.some(
                  (task) =>
                      (task.statusCode === TaskStatus.PENDING || task.statusCode === TaskStatus.QUEUED) &&
                      task.scheduledDateTime !== null &&
                      task.scheduledDateTime > now,
              )
            : null;

    const activeOrPendingStatuses: string[] = [TaskStatus.PENDING, TaskStatus.QUEUED, TaskStatus.IN_PROGRESS];
    const showRequeueWarning =
        !tasksLoading && (dueTask === null || !activeOrPendingStatuses.includes(dueTask.statusCode));

    return (
        <>
            <IotVisionDialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                title={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <TypeIcon sx={{ fontSize: 24, color: iconColor }} />
                        <Box>
                            <Typography variant="h6" lineHeight={1.3}>
                                {row.typeLabel}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                {row.id}
                            </Typography>
                        </Box>
                    </Box>
                }
            >
                <Grid container spacing={2}>
                    <MetaField label="API Status">
                        <ScheduleStatusChip status={row.status} />
                    </MetaField>
                    <MetaField label="Derived Status">
                        {derivedStatus ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <ScheduleStatusChip status={derivedStatus} />
                                {hasNextTask === false ? (
                                    <IotVisionChip
                                        icon={<WarningAmberOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                                        label="No next task"
                                        size="small"
                                        variant="filled"
                                        sx={{
                                            minWidth: "auto",
                                            backgroundColor: "error.light",
                                            color: "error.main",
                                            border: "1px solid",
                                            borderColor: "error.main",
                                            height: "24px",
                                            fontWeight: 600,
                                            "& .MuiChip-icon": {
                                                color: "error.main",
                                            },
                                        }}
                                    />
                                ) : null}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {EM_DASH}
                            </Typography>
                        )}
                    </MetaField>
                    <MetaField label="Start">
                        <DateTimeCell date={row.startTime} />
                    </MetaField>
                    <MetaField label="End">
                        <DateTimeCell date={row.endTime} emptyLabel={NO_END_LABEL} />
                    </MetaField>
                    <MetaField label="Repeats">
                        <Typography variant="body2">{displayOrDash(row.repeats)}</Typography>
                    </MetaField>
                    <MetaField label="Modifier">
                        <Typography variant="body2">{displayOrDash(row.modifier)}</Typography>
                    </MetaField>
                    <MetaField label="Created">
                        <DateTimeCell date={row.createdAt} />
                    </MetaField>
                    <MetaField label="Schedule ID">
                        <Typography variant="body2" fontFamily="monospace">
                            {row.id}
                        </Typography>
                    </MetaField>
                </Grid>
                {showRequeueWarning ? (
                    <Box
                        sx={{
                            mt: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 2,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "error.light",
                            backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
                        }}
                    >
                        <WarningAmberOutlinedIcon color="error" fontSize="small" sx={{ mt: "2px" }} />
                        <Box>
                            <Typography variant="body2" fontWeight={600} color="error.dark">
                                Schedule appears failed
                            </Typography>
                            <Typography variant="body2" color="error.dark" sx={{ opacity: 0.8 }}>
                                The most recent task did not complete successfully. Re-queuing will create the next
                                task without altering history.
                            </Typography>
                        </Box>
                    </Box>
                ) : null}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Task history
                    </Typography>
                    <IotVisionTable
                        rows={taskRows}
                        columns={taskHistoryColumns}
                        rowCount={taskRows.length}
                        loading={tasksLoading}
                        hideFooter
                        disableColumnSelector
                        components={{ NoRowsOverlay: TaskHistoryPlaceholder }}
                        componentsProps={{
                            noRowsOverlay: { message: tasksError ?? NO_TASKS_MESSAGE },
                        }}
                        sx={{
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "grey.100",
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: 600,
                                color: "text.secondary",
                            },
                        }}
                    />
                </Box>
            </IotVisionDialog>
            <DataViewDialog
                open={taskDataDialogOpen}
                title="Task data"
                data={selectedTaskData}
                onClose={() => {
                    setTaskDataDialogOpen(false);
                    setSelectedTaskData(null);
                }}
            />
        </>
    );
};

export default ScheduleDetailModal;
